import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Grid,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import {
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import moment from "moment";
import axios from "axios";

export default function ApprovalRequestList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState(""); // "approve" or "reject"
  const [actionNotes, setActionNotes] = useState("");

  const { data, isLoading, isError, error, refetch } = useFetchObjects(
    ["approval-requests", page, rowsPerPage, searchTerm, statusFilter],
    `approval-requests/?page=${page}&page_size=${rowsPerPage}&search=${searchTerm}&status=${statusFilter}`,
    token
  );

  const requests = data?.results || [];
  const totalCount = data?.count || 0;

  const getStatusColor = (status) => {
    switch (status) {
      case 1: // Pending
        return "warning";
      case 2: // Approved
        return "success";
      case 3: // Rejected
        return "error";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 1:
        return "Pending";
      case 2:
        return "Approved";
      case 3:
        return "Rejected";
      default:
        return "Unknown";
    }
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setActionType("approve");
    setActionNotes("");
    setActionDialogOpen(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setActionType("reject");
    setActionNotes("");
    setActionDialogOpen(true);
  };

  const handleActionConfirm = async () => {
    if (!selectedRequest) return;

    try {
      const url = `approval-requests/${selectedRequest.id}/${actionType}/`;
      const payload = actionType === "approve" 
        ? { notes: actionNotes || "Approved" }
        : { reason: actionNotes || "Rejected" };

      await axios.post(
        `${process.env.REACT_APP_API_URL || "http://localhost:9000/api"}/${url}`,
        payload,
        {
          headers: {
            Authorization: `Token ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setActionDialogOpen(false);
      setSelectedRequest(null);
      setActionNotes("");
      refetch();
    } catch (err) {
      console.error("Error processing approval:", err);
      alert(err?.response?.data?.error || err?.response?.data?.detail || "Failed to process approval");
    }
  };

  const handleActionCancel = () => {
    setActionDialogOpen(false);
    setSelectedRequest(null);
    setActionNotes("");
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error?.message || "Failed to load approval requests"}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              Approval Requests
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <TextField
              size="small"
              placeholder="Search requests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant={statusFilter === "" ? "contained" : "outlined"}
              onClick={() => setStatusFilter("")}
              size="small"
            >
              All
            </Button>
            <Button
              variant={statusFilter === "1" ? "contained" : "outlined"}
              onClick={() => setStatusFilter("1")}
              size="small"
              color="warning"
            >
              Pending
            </Button>
            <Button
              variant={statusFilter === "2" ? "contained" : "outlined"}
              onClick={() => setStatusFilter("2")}
              size="small"
              color="success"
            >
              Approved
            </Button>
            <Button
              variant={statusFilter === "3" ? "contained" : "outlined"}
              onClick={() => setStatusFilter("3")}
              size="small"
              color="error"
            >
              Rejected
            </Button>
          </Grid>
        </Grid>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Request ID</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Entity</strong></TableCell>
                  <TableCell><strong>Requested By</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No approval requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((request) => (
                    <TableRow key={request.id} hover>
                      <TableCell>#{request.id}</TableCell>
                      <TableCell>{request.request_type_display || "N/A"}</TableCell>
                      <TableCell>
                        {request.entity_type_display || "N/A"} #{request.entity_id}
                      </TableCell>
                      <TableCell>{request.requested_by_name_display || "System"}</TableCell>
                      <TableCell>
                        {moment(request.created_at).format("YYYY-MM-DD HH:mm")}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(request.status)}
                          color={getStatusColor(request.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {request.status === 1 && (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApprove(request)}
                              title="Approve"
                            >
                              <CheckCircleIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleReject(request)}
                              title="Reject"
                            >
                              <CancelIcon />
                            </IconButton>
                          </Box>
                        )}
                        {request.status !== 1 && (
                          <Typography variant="body2" color="text.secondary">
                            {request.status === 2 ? "Approved" : "Rejected"} by{" "}
                            {request.approved_by_name_display || request.rejected_by_name_display || "N/A"}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {totalCount > 0 && (
          <TablePagination
            component="div"
            count={totalCount}
            page={page - 1}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Paper>

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={handleActionCancel} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === "approve" ? "Approve Request" : "Reject Request"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to {actionType} this approval request?
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes (optional)"
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            placeholder={`Enter notes for ${actionType === "approve" ? "approval" : "rejection"}...`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleActionCancel}>Cancel</Button>
          <Button
            onClick={handleActionConfirm}
            variant="contained"
            color={actionType === "approve" ? "success" : "error"}
          >
            {actionType === "approve" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
